package org.teddapp.project.db

import com.tedd.db.Task
import com.tedd.db.TeddDatabase
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.withContext

/**
 * Cascade de recalcul de progression (spec OBJECTIFS §7.1–7.3).
 *
 * Règle :
 * - Progression d'un jalon = ARRONDI(actions "done" / total actions × 100). 0 si aucune action.
 * - Progression d'un objectif = ARRONDI(moyenne des progressions de ses jalons). 0 si aucun jalon.
 * - À chaque écriture de progression d'objectif, on append à l'historique si la valeur a changé.
 *
 * Distinction explicite avec la surcharge manuelle (spec §7.4) :
 * l'utilisateur peut écraser la progression via `ObjectiveRepository.updateProgress`
 * (boutons ±10%) — cette route ne passe pas par ce service et n'écrit pas d'historique.
 *
 * Toutes les méthodes sont thread-safe via `Dispatchers.Default`.
 */
class ProgressService(
    private val db: TeddDatabase,
    private val history: org.teddapp.project.db.repo.ObjectiveProgressHistoryRepository,
) {

    /**
     * Recalcule la progression du jalon donné, puis propage à l'objectif parent.
     * Retourne la nouvelle progression du jalon.
     */
    suspend fun recalcMilestone(milestoneId: String): Long = withContext(Dispatchers.Default) {
        val counts = db.taskQueries
            .countByParent(ParentKind.Milestone.raw, milestoneId)
            .executeAsOne()
        val total = counts.total
        val done = counts.done ?: 0L
        val pct = if (total == 0L) 0L else ((done * 100.0 / total) + 0.5).toLong().coerceIn(0L, 100L)

        val now = nowMillis()
        db.milestoneQueries.updateProgress(pct, now, milestoneId)

        val milestone = db.milestoneQueries.selectById(milestoneId).executeAsOneOrNull()
        if (milestone != null) {
            recalcObjective(milestone.objective_id)
        }
        pct
    }

    /**
     * Recalcule la progression de l'objectif donné depuis ses jalons.
     * Écrit l'historique uniquement si la valeur a changé.
     */
    suspend fun recalcObjective(objectiveId: String): Long = withContext(Dispatchers.Default) {
        val milestones = db.milestoneQueries.selectByObjective(objectiveId).executeAsList()
        val pct = if (milestones.isEmpty()) {
            0L
        } else {
            val sum = milestones.sumOf { it.progress }
            ((sum.toDouble() / milestones.size) + 0.5).toLong().coerceIn(0L, 100L)
        }

        val current = db.objectiveQueries.selectById(objectiveId).executeAsOneOrNull()
        val previous = current?.progress
        val now = nowMillis()
        db.objectiveQueries.updateProgress(pct, now, objectiveId)

        if (previous != pct) {
            history.append(objectiveId, pct, now)
        }
        pct
    }

    /**
     * Cascade complète au cochage d'une action (spec §7.3).
     * Bascule done <-> pending, met à jour completed_at, puis propage.
     */
    suspend fun toggleTask(task: Task) = withContext(Dispatchers.Default) {
        val now = nowMillis()
        if (task.status == "done") {
            db.taskQueries.markPending(now, task.id)
        } else {
            db.taskQueries.markDone(now, now, task.id)
            // Une micro-tâche récurrente ne reste pas cochée : elle revient à sa prochaine date.
            RecurrenceScheduler.onCompleted(db, task.id)
        }
        if (task.parent_kind == ParentKind.Milestone.raw && task.parent_id != null) {
            recalcMilestone(task.parent_id!!)
        }
        notifyLeafIfChanged(db, task)
    }

    /**
     * Force l'action à un statut explicite (pending / done / skipped) puis propage.
     * Spec §3.3 : "skipped" reste comptabilisé au dénominateur — pénalise la progression.
     */
    suspend fun setTaskStatus(task: Task, status: String) = withContext(Dispatchers.Default) {
        val now = nowMillis()
        when (status) {
            "done" -> db.taskQueries.markDone(now, now, task.id)
            "skipped" -> db.taskQueries.markSkipped(now, task.id)
            "pending" -> db.taskQueries.markPending(now, task.id)
            else -> return@withContext
        }
        if (task.parent_kind == ParentKind.Milestone.raw && task.parent_id != null) {
            recalcMilestone(task.parent_id!!)
        }
        notifyLeafIfChanged(db, task)
    }

    /**
     * À appeler après création d'une action sur un jalon.
     */
    suspend fun onTaskCreated(milestoneId: String) {
        recalcMilestone(milestoneId)
    }

    /**
     * À appeler après soft-delete d'une action.
     * Doit recevoir le milestoneId mémorisé AVANT la suppression.
     */
    suspend fun onTaskDeleted(milestoneId: String) {
        recalcMilestone(milestoneId)
    }

    /**
     * Déplacement d'une action vers un autre jalon (spec §7.6).
     * Recalcule l'ancien et le nouveau jalon (et donc leurs objectifs).
     */
    suspend fun onTaskMoved(oldMilestoneId: String?, newMilestoneId: String?) {
        if (oldMilestoneId != null) recalcMilestone(oldMilestoneId)
        if (newMilestoneId != null && newMilestoneId != oldMilestoneId) {
            recalcMilestone(newMilestoneId)
        }
    }

    /**
     * À appeler après création/suppression d'un jalon — recalcule l'objectif.
     */
    suspend fun onMilestoneChanged(objectiveId: String) {
        recalcObjective(objectiveId)
    }

    /**
     * Force la progression d'un objectif à 100 et son statut à "done" (spec §3.1 + §11.3).
     * Écrit l'historique car la valeur change (sauf si déjà 100).
     */
    suspend fun markObjectiveDone(objectiveId: String) = withContext(Dispatchers.Default) {
        val current = db.objectiveQueries.selectById(objectiveId).executeAsOneOrNull() ?: return@withContext
        val now = nowMillis()
        db.objectiveQueries.update(
            current.category_id,
            current.title,
            current.description,
            current.motivation,
            current.deadline,
            current.year,
            "done",
            100L,
            now,
            objectiveId,
        )
        if (current.progress != 100L) {
            history.append(objectiveId, 100L, now)
        }
    }
}
